import { NgService } from "@app/di/decorators";
import { Token } from "@app/di/token";
import { eClusterEnv, eSkin, IRootScope, ITheme } from "@models";
import { KPromise } from "@rules";
import { Color } from "@tools";
import { StateService, Transition, TransitionService } from "@uirouter/angularjs";

@NgService({
    token: Token.ThemeService,
    dependencies: [
        Token.$RootScope,
        Token.$Http,
        Token.$Location,
        Token.$Transitions,
        Token.$State
    ]
})
export class ThemeService {

    constructor(
        private $rootScope: IRootScope,
        private $http: ng.IHttpService,
        private $location: ng.ILocationService,
        private $transitions: TransitionService,
        private $state: StateService
    ) {
        this.defaultThemes = [
            {
                id: null,
                name: "Default",
                skin: eSkin.material,
                background: "#f2f2f2",
                input: "#FFFFFF",
                header: "#025064",
                primary: "#025064",
                accent: "#ff8873", //#FF9800", //"#ffd54f", //"#ff9800", //"#ef6c00", //"#ff8f00", //"#E3831C",
                warn: "#ff2102",
                success: "#049e8a",
                border: "#CCC",
                dark: false,
                font: "Roboto",
                fontSize: 15,
                radius: 4
            },
            {
                id: -1,
                name: "Night Launch",
                skin: eSkin.material,
                background: "#1a1a1a",
                input: "#242424",
                header: "#bfbfbf",
                primary: "#424242",
                accent: "#ffaf47",
                warn: "#e0583a",
                success: "#22a153",
                border: "#575757",
                dark: true,
                font: "Roboto",
                fontSize: 15,
                radius: 2
            },
            {
                id: -2,
                name: "Valyrian Steel",
                skin: eSkin.material,
                background: "#eeeeee",
                input: "#FFFFFF",
                header: "#535863",
                primary: "#535863",
                accent: "#ff5e00",
                warn: "#e33e1c",
                success: "#14a54a",
                border: "#b6c2e0",
                dark: false,
                font: "Roboto",
                fontSize: 15,
                radius: 2
            },
            {
                id: -3,
                name: "Midnight Bananas",
                skin: eSkin.classic,
                background: "#2b2b2b",
                input: "#2b2b2b",
                header: "#33702b",
                primary: "#33702b",
                accent: "#e7f043",
                warn: "#e33e1c",
                success: "#14a54a",
                border: "#a6ffad",
                dark: true,
                font: "Roboto",
                fontSize: 15,
                radius: 2
            },
            {
                id: -4,
                name: "Blood Money",
                skin: eSkin.material,
                background: "#eeeeee",
                input: "#ffffff",
                header: "#8c3333",
                primary: "#8c3333",
                accent: "#1F5454",
                warn: "#e33e1c",
                success: "#297029",
                border: "#b8d6d6",
                dark: false,
                font: "Roboto",
                fontSize: 15,
                radius: 2
            },
            {
                id: -5,
                name: "Apple II",
                skin: eSkin.classic,
                background: "#000000",
                input: "#000000",
                header: "#141414",
                primary: "#141414",
                accent: "#33fe33",
                warn: "#e33e1c",
                success: "#14a54a",
                border: "#33fe33",
                dark: true,
                font: "Roboto",
                fontSize: 15,
                radius: 2
            },
            {
                id: -6,
                name: "Salesforce Lightning",
                skin: eSkin.material,
                background: "#f7f7f7",
                input: "#ffffff",
                header: "#16325C",
                primary: "#16325C",
                accent: "#00A1E0",
                warn: "#e33e1c",
                success: "#14a54a",
                border: "#d8dde6",
                dark: false,
                font: "Roboto",
                fontSize: 15,
                radius: 2
            },
            {
                id: -7,
                name: "Classic",
                skin: eSkin.material,
                background: "#f2f2f2",
                input: "#FFFFFF",
                header: "#084860",
                primary: "#084860", //#607D8B", //"#7986cb", //"#0097a7", //"#304ffe", //"#0d47a1", //"#084860",
                accent: "#E3831C", //#FF9800", //"#ffd54f", //"#ff9800", //"#ef6c00", //"#ff8f00", //"#E3831C",
                warn: "#E33E1C",
                success: "#14A54A",
                border: "#CCC",
                dark: false,
                font: "Roboto",
                fontSize: 15,
                radius: 4
            },
        ];

        let queryString = $location.search();

        this.loadTemplate().then(() => {
            this.parseAndLoad(this.getActiveTheme());
        });

        $transitions.onFinish({}, (transition: Transition) => {
            let from = transition.from().name;
            let to = transition.to().name;
            if ((to == "sceneEdit" && from != "sceneEdit") || from == "sceneEdit"  && to != "sceneEdit") {
                this.parseAndLoad(this.getActiveTheme());
            }
        });
    }

    public defaultThemes: ITheme[];
    public themeCss: string;

    private loadTemplate(): Q.IPromise<any> {
        // in debug, we get the css from the server so that it can be changed without stopping debugging
        if (this.$rootScope.clusterEnv == eClusterEnv.dev) {
            return this.$http.get<string>("kbmax-theme.css").then(r => {
                return this.themeCss = r.data;
            });
        } else {
            this.themeCss = this.$rootScope.context.themeCss;
            return new KPromise(this.themeCss) as Q.IPromise<any>;
        }
    }

    public getDefaultTheme(id: number = null) {
        return this.defaultThemes.find(t => t.id == id);
    }

    public getActiveTheme(): ITheme {
        let queryString = this.$location.search();

        if (queryString["idtheme"] && !this.$rootScope.context.sfdcCanvasRequest) {
            let idTheme = Number(queryString["idtheme"]);
            if (idTheme > 0) {
                return this.$rootScope.context.theme;
            } else {
                return this.getDefaultTheme(idTheme);
            }
        }
        else if (this.$rootScope.context.sfdcCanvasRequest) {

            var sfTheme = this.getDefaultTheme(-6);
            //if they have a non-default theme setup, then we use the skin from that.  This way they get the salesforce theme,
            //but with the skin they want (material or classic)
            if (this.$rootScope.companySettings.idTheme && this.$rootScope.companySettings.idTheme > 0) {
                sfTheme.skin = this.$rootScope.context.theme.skin;
            }
            //let overrideTheme = await this.$apiService.themes.getById(1);
            //sfTheme = overrideTheme;
            if (queryString['idtheme']) {
                return this.$rootScope.context.theme ?? sfTheme;
            }
            
            console.log(sfTheme);
            return sfTheme;
        }
        else if (
            (this.$state.current && this.$state.current.name == "sceneEdit" && !this.$state.transition)
            ||  (this.$state.transition && (this.$state.transition.to().name == "sceneEdit"))
        ) {
            return this.getDefaultTheme(-1);
        }
        else if (this.$rootScope.context.theme) {
            return this.$rootScope.context.theme;
        } else {
            return this.getDefaultTheme(this.$rootScope.companySettings.idTheme);
        }
    }

    public getRgbString(theme: ITheme, palette: string, shade: number, opacity: number, contrast: boolean, grayscale: boolean) {
        let c: Color = new Color(theme[palette]);

        let offset = shade - 50;
        if (theme.dark) offset = -offset;
        let percent = Math.abs(offset / 2);
        if (offset > 0) {
            c = Color.darken(c.getOriginalInput(), percent);
        } else if (offset < 0) {
            c = Color.lighten(c.getOriginalInput(), percent);
        }

        if (contrast) {
            if (c.isDark()) {
                c = new Color({ r: 250, g: 250, b: 250 });
            } else {
                c = new Color({ r: 17, g: 17, b: 17 });
            }
        }
        if (grayscale) {
            let hsv = c.toHsv();
            hsv.s = 0;
            c = new Color(hsv);
        }

        c.setAlpha(opacity);

        return c.toRgbString();
    }

    public parseTheme(theme?: ITheme): string {
        theme = theme || this.getDefaultTheme();
        let hueRegex = new RegExp(
            // tslint:disable-next-line:max-line-length
            '(\'|")?{{\\s*(background|input|primary|accent|warn|success|border|header)_(color|contrast|grayscale)_?(\\d\\.?\\d*)?_?(\\d\\.?\\d*)?\\s*}}(\"|\')?', "g"
        );
        let css = this.themeCss.replace(
            hueRegex,
            (match: string, quotes: string, palette: string, cType: string, shade: string, opacity: string) => {
                let shadeNum: number = shade ? Number(shade) : 50;
                let opacityNum: number = opacity == null ? 1 : Number(opacity) / 100;
                return this.getRgbString(theme, palette, shadeNum, opacityNum, (cType == "contrast"), (cType == "grayscale"));
            });

        // border-radius
        css = css.replaceAll("'{{radius}}'", theme.radius + "px");
        css = css.replaceAll("'{{fontSize}}'", theme.fontSize + "px");
        
        // font
        if (theme.font) {
            css = css.replaceAll("{{font}}", theme.font);
        }

        if (theme.customFontUrl) {
            css = `@import url('${theme.customFontUrl}');\n` + css;
        }

        return css;
    }

    public setThemeToHead(themeCss: string) {
        let style = document.getElementById("kbmax-theme") as HTMLStyleElement;
        if (!style) {
            style = document.createElement("style");
            style.id = "kbmax-theme";
        }
        style.type = "text/css";
        style.innerHTML = themeCss;
        if (!style.parentElement) {
            document.head.appendChild(style);
        }
    }

    public refreshActiveTheme() {
        this.parseAndLoad(this.getActiveTheme());
    }

    public parseAndLoad(theme?: ITheme) {
        let css = this.parseTheme(theme);
        this.setThemeToHead(css);
        this.$rootScope.currentTheme = theme;
    }
}
